import routes from '@/content/germany/routes.json';
import type { HeadMeta } from './head';

export const GERMANY_ORIGIN = 'https://health-prime.org';
export const SWISS_ORIGIN = 'https://health-prime.ch';
export const GERMANY_ROUTES = routes.map((r) => r.de);
export const GERMANY_NAV = routes.slice(1).map((r) => ({ label: r.label, href: r.de }));
export const isGermanyPath = (path: string) => /^\/de-de(?:\/|$)/.test(path);
export const swissPath = (path: string) => routes.find(r => r.de === path)?.ch ?? (path.replace(/^\/de-de(?=\/|$)/, '') || '/');
export function germanyPath(path: string) {
  const [pathname, ...suffix] = path.split(/(?=[?#])/);
  return (routes.find(r => r.ch === pathname)?.de ?? pathname) + suffix.join('');
}

/** Only genuine equivalent country pages participate in regional alternates. */
export function marketMeta(meta: HeadMeta, germany = false): HeadMeta {
  const path = new URL(meta.canonical).pathname.replace(/\/+$/, '') || '/';
  const pair = routes.find((r) => r.de === path || r.ch === path);
  if (!pair) return meta;
  const german = germany || isGermanyPath(path);
  const canonical = `${german ? GERMANY_ORIGIN : SWISS_ORIGIN}${german ? pair.de : pair.ch}`;
  const lang = pair.ch.startsWith('/en/') ? 'en' : 'de';
  const localize = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(localize);
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, localize(item)]));
    if (typeof value === 'string' && value.startsWith(SWISS_ORIGIN)) {
      const url = new URL(value);
      const paired = routes.find(r => r.ch === url.pathname);
      if (paired) return GERMANY_ORIGIN + paired.de + url.search + url.hash;
    }
    if (value === 'de-CH') return 'de-DE';
    if (value === 'en-CH') return 'en-DE';
    return value;
  };
  const otherLanguages = (meta.alternates ?? []).filter(a => a.language !== 'x-default' && !a.language.startsWith(lang)).flatMap(a => {
    const linked = routes.find(r => SWISS_ORIGIN + r.ch === a.href);
    return linked ? [a, { language: a.language.replace(/-CH$/, '-DE'), href: GERMANY_ORIGIN + linked.de }] : [a];
  });
  return {
    ...meta,
    canonical,
    language: `${lang}-${german ? 'DE' : 'CH'}`,
    jsonLd: german && meta.jsonLd ? localize(meta.jsonLd) as Record<string, unknown>[] : meta.jsonLd,
    alternates: [
      { language: `${lang}-CH`, href: SWISS_ORIGIN + pair.ch },
      { language: `${lang}-DE`, href: GERMANY_ORIGIN + pair.de },
      ...otherLanguages,
      { language: 'x-default', href: SWISS_ORIGIN + pair.ch },
    ],
  };
}
